import React, { useState } from 'react';
import { XMarkIcon, PaperAirplaneIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { assistantChat } from '../../services/openaiService';

// AI Assistant component for chat - provides AI-powered Q&A during meetings
export default function AIChatAssistant({ isOpen, onClose, meetingContext, transcriptHistory = [], chatMessages = [] }) {
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleAsk = async () => {
    if (!question.trim()) return;

    const userMessage = { role: 'user', content: question, timestamp: new Date().toISOString() };
    setConversation(prev => [...prev, userMessage]);
    setQuestion('');
    setIsLoading(true);

    try {
      // Detect if query is urgent
      const isUrgent = detectUrgency(question);

      // Build enhanced context with transcript and chat data
      const enhancedContext = {
        ...meetingContext,
        // Include full voice transcript for meeting content questions (up to 200 entries)
        recentTranscript: transcriptHistory.slice(-200).map(t => ({
          speaker: t.speaker,
          text: t.text,
          time: t.time
        })),
        // Include recent chat messages (up to 100)
        recentChatMessages: chatMessages.slice(-100).map(m => ({
          sender: m.user || m.senderName || m.senderId || 'Unknown',
          text: m.text || m.message || '',
          time: m.timestamp ? new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
          isPrivate: m.channel && m.channel !== 'Everyone'
        })).filter(m => m.text && !m.isPrivate) // Only include public messages with content
      };

      // Call OpenAI through our backend
      const aiResponse = await assistantChat(
        question,
        conversation, // Pass conversation history for context
        enhancedContext
      );

      const aiMessage = {
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString(),
        isUrgent
      };
      setConversation(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('AI assistant error:', error);
      setConversation(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again or ask the host directly.',
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const detectUrgency = (message) => {
    const urgentKeywords = ['urgent', 'emergency', 'help', 'issue', 'problem', 'not working', 'broken', 'error', 'stuck', "can't", "won't"];
    const lowerMessage = message.toLowerCase();
    return urgentKeywords.some(keyword => lowerMessage.includes(keyword));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  const clearConversation = () => {
    setConversation([]);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed bottom-4 right-4 w-80 sm:w-96 bg-[#1a1f35] rounded-xl shadow-2xl border border-white/10 flex flex-col z-[9999]"
      style={{ height: '450px', maxHeight: 'calc(100vh - 120px)' }}
    >
      {/* Header */}
      <div className="p-3 border-b border-white/10 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <SparklesIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm flex items-center gap-1.5">
              AI Assistant
              <span className="text-[9px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded font-normal">
                Powered by OpenAI
              </span>
            </h3>
            <p className="text-[10px] text-gray-400">
              {transcriptHistory.length > 0 ? `Meeting context: ${transcriptHistory.length} transcript entries` : 'Ask me anything about this meeting'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-white/10 rounded transition-colors"
        >
          <XMarkIcon className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* Conversation */}
      <div className="flex-1 overflow-y-auto p-3">
        {conversation.length === 0 ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-500/20 flex items-center justify-center">
              <SparklesIcon className="w-6 h-6 text-purple-400" />
            </div>
            <p className="text-gray-400 text-sm">Ask me anything about this meeting!</p>
            <p className="text-gray-500 text-xs mt-2">
              {transcriptHistory.length > 0
                ? `I have context from ${transcriptHistory.length} transcript entries and ${chatMessages.length} chat messages.`
                : 'I can answer questions about the discussion, summarize topics, and help with platform features.'}
            </p>
            <div className="mt-4 space-y-2">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Try asking:</p>
              <div className="flex flex-wrap gap-1.5 justify-center">
                {(transcriptHistory.length > 0
                  ? ['Summarize the discussion so far', 'What were the key decisions?', 'What action items were mentioned?']
                  : ['What has been discussed?', 'Summarize the chat', 'How do I share my screen?']
                ).map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => setQuestion(suggestion)}
                    className="text-[10px] px-2 py-1 bg-purple-500/10 text-purple-300 rounded-full hover:bg-purple-500/20 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {conversation.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <SparklesIcon className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-lg p-2.5 ${
                  msg.role === 'user'
                    ? 'bg-[#6a1fbf] text-white'
                    : msg.error
                    ? 'bg-red-500/20 text-red-300'
                    : 'bg-[#252b45] text-white'
                }`}>
                  {msg.isUrgent && msg.role === 'assistant' && (
                    <div className="flex items-center gap-1 text-[10px] text-yellow-400 mb-1.5">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Flagged as urgent for host
                    </div>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  <p className="text-[9px] opacity-60 mt-1">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-2 justify-start">
                <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <SparklesIcon className="w-3 h-3 text-white animate-pulse" />
                </div>
                <div className="bg-[#252b45] rounded-lg p-2.5">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer with clear button */}
      {conversation.length > 0 && (
        <div className="px-3 pb-1">
          <button
            onClick={clearConversation}
            className="text-[10px] text-gray-500 hover:text-gray-300 transition-colors"
          >
            Clear conversation
          </button>
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t border-white/10 flex-shrink-0">
        <div className="flex gap-2">
          <input
            type="text"
            name="rlink-chat-message"
            autoComplete="off"
            data-form-type="other"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask a question..."
            className="flex-1 bg-[#252b45] border border-white/10 text-white placeholder:text-gray-500 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
            disabled={isLoading}
          />
          <button
            onClick={handleAsk}
            disabled={!question.trim() || isLoading}
            className="p-2 bg-[#6a1fbf] hover:bg-[#7a2fcf] disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {isLoading ? (
              <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <PaperAirplaneIcon className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
